import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';

interface ValuationRangeProps {
  min: number;
  max: number;
  baseMin: number;
  baseMax: number;
  status: 'loading' | 'ready' | 'unavailable';
}

const formatAmount = (value: number): string =>
  Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useCount = (target: number, active: boolean): number => {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    if (!active) return undefined;
    if (prefersReducedMotion()) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }

    const from = fromRef.current;
    const start = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / 400);
      const eased = 1 - (1 - progress) ** 3;
      setShown(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, active]);

  return shown;
};

const ValuationRange: React.FC<ValuationRangeProps> = ({
  min,
  max,
  baseMin,
  baseMax,
  status,
}) => {
  const ready = status === 'ready' && min > 0 && max > 0 && Number.isFinite(min) && Number.isFinite(max);
  const shownMin = useCount(min, ready);
  const shownMax = useCount(max, ready);

  if (status === 'unavailable') return null;
  if (status === 'ready' && !ready) return null;

  const span = baseMax - baseMin;
  const width = span > 0 ? Math.min(100, Math.max(8, ((max - min) / span) * 100)) : 100;
  const inset = (100 - width) / 2;
  const announcement = ready
    ? `Stiamo calcolando il tuo importo: ${formatAmount(min)} – ${formatAmount(max)} €`
    : 'Stiamo calcolando il tuo importo';

  return (
    <div className={styles.range}>
      <p className={styles.live} aria-live="polite">
        {announcement}
      </p>
      <div className={styles.row} aria-hidden="true">
        <span className={styles.label}>Stiamo calcolando il tuo importo</span>
        {ready && (
          <span className={styles.figures}>
            {formatAmount(shownMin)} – {formatAmount(shownMax)} €
          </span>
        )}
      </div>
      {ready && (
        <div className={styles.track} aria-hidden="true">
          <span className={styles.fill} style={{ left: `${inset}%`, right: `${inset}%` }} />
        </div>
      )}
    </div>
  );
};

export default ValuationRange;
